// Fragment pack bytes -> plain typed arrays. No three.js, no imports.
//
// The one implementation of the pack format on the viewer side, used in
// two places: packFormat.decodePack on the main thread (fallback, tests)
// and public/packDecodeWorker.js, which is GENERATED from this file by
// scripts/buildPackWorker.js so pack decoding runs off the main thread
// (measured 2026-09-16: ~28 ms of main thread per SpaceX pack). Keep this
// file free of imports and of anything but `export function` / `export
// const` declarations - the generator strips the word `export`, and
// packDecodeWorker.test.js fails if the worker's copy drifts from this one.
export const PACK_MAGIC = 0x4b504c42; // "BLPK"
export const PACK_VERSIONS = [1, 2];

const PACK_ARRAYS = {
  5120: Int8Array, 5121: Uint8Array, 5122: Int16Array, 5123: Uint16Array, 5125: Uint32Array, 5126: Float32Array,
};
const PACK_BYTES = { 5120: 1, 5121: 1, 5122: 2, 5123: 2, 5125: 4, 5126: 4 };

function packDecodeAttribute(buffer, dataOffset, entry, decoder) {
  const [name, componentType, itemSize, normalized, offset, count, codec] = entry;
  const Ctor = PACK_ARRAYS[componentType];
  if (!Ctor) throw new Error('pack: unknown component type ' + componentType);
  if (!codec) {
    return { name, array: new Ctor(buffer, dataOffset + offset, count * itemSize), itemSize, normalized: !!normalized };
  }
  const src = new Uint8Array(buffer, dataOffset + offset, codec.len);
  const raw = new Uint8Array(count * codec.vs);
  decoder.decodeVertexBuffer(raw, count, codec.vs, src);
  if (codec.q === 'u16') {
    const q = new Uint16Array(raw.buffer, 0, count * 4);
    const out = new Float32Array(count * 3);
    const mx = codec.min[0]; const my = codec.min[1]; const mz = codec.min[2];
    const sx = codec.scale[0]; const sy = codec.scale[1]; const sz = codec.scale[2];
    for (let i = 0; i < count; i++) {
      out[i * 3] = mx + q[i * 4] * sx;
      out[i * 3 + 1] = my + q[i * 4 + 1] * sy;
      out[i * 3 + 2] = mz + q[i * 4 + 2] * sz;
    }
    return { name, array: out, itemSize: 3, normalized: false };
  }
  if (codec.q === 'oct8') {
    const q = new Int8Array(raw.buffer, 0, count * 4);
    const out = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      let x = q[i * 4] / 127;
      let y = q[i * 4 + 1] / 127;
      const z = 1 - Math.abs(x) - Math.abs(y);
      if (z < 0) {
        const ox = x;
        x = (1 - Math.abs(y)) * (ox >= 0 ? 1 : -1);
        y = (1 - Math.abs(ox)) * (y >= 0 ? 1 : -1);
      }
      const len = Math.hypot(x, y, z) || 1;
      out[i * 3] = x / len; out[i * 3 + 1] = y / len; out[i * 3 + 2] = z / len;
    }
    return { name, array: out, itemSize: 3, normalized: false };
  }
  const size = PACK_BYTES[componentType] * itemSize;
  let bytes = raw;
  if (codec.vs !== size) {
    bytes = new Uint8Array(count * size);
    for (let i = 0; i < count; i++) bytes.set(raw.subarray(i * codec.vs, i * codec.vs + size), i * size);
  }
  return { name, array: new Ctor(bytes.buffer, bytes.byteOffset, count * itemSize), itemSize, normalized: !!normalized };
}

function packDecodeIndex(buffer, dataOffset, entry, decoder) {
  const [componentType, offset, count, codec] = entry;
  const Ctor = PACK_ARRAYS[componentType];
  if (!codec) return new Ctor(buffer, dataOffset + offset, count);
  const size = PACK_BYTES[componentType];
  const out = new Uint8Array(count * size);
  decoder.decodeIndexBuffer(out, count, size, new Uint8Array(buffer, dataOffset + offset, codec.len));
  return new Ctor(out.buffer, 0, count);
}

/**
 * Decode a pack. `decoder` is a ready MeshoptDecoder (needed for v2 only).
 * -> { version, level, materials, fragments: [{ h, n, m, attrs: [{ name, array, itemSize, normalized }], index }] }
 */
export function decodePackArrays(buffer, decoder) {
  const head = new DataView(buffer, 0, 12);
  if (head.getUint32(0, true) !== PACK_MAGIC) throw new Error('pack: bad magic');
  const version = head.getUint32(4, true);
  if (PACK_VERSIONS.indexOf(version) === -1) throw new Error('pack: unsupported version ' + version);
  const tocLength = head.getUint32(8, true);
  const toc = JSON.parse(new TextDecoder().decode(new Uint8Array(buffer, 12, tocLength)));
  const dataOffset = 12 + tocLength;
  const fragments = [];
  for (const f of toc.f || []) {
    fragments.push({
      h: f.h,
      n: f.n || '',
      m: f.m,
      attrs: f.a.map((entry) => packDecodeAttribute(buffer, dataOffset, entry, decoder)),
      index: f.i ? packDecodeIndex(buffer, dataOffset, f.i, decoder) : null,
    });
  }
  return { version, level: toc.level, materials: toc.m || [], fragments };
}
